
// ─── Permission constants ─────────────────────────────────────────────────────
// User Management  (mirrors /api/admin/users/*)
export const MANAGE_USERS        = 'MANAGE_USERS';
export const VIEW_USERS          = 'VIEW_USERS';
export const CREATE_USERS        = 'CREATE_USERS';
export const EDIT_USERS          = 'EDIT_USERS';
export const DELETE_USERS        = 'DELETE_USERS';
export const RESET_PASSWORD      = 'RESET_PASSWORD';
export const TOGGLE_USER_STATUS  = 'TOGGLE_USER_STATUS';

// Platform Config  (mirrors /api/admin/platforms/*)
export const MANAGE_PLATFORMS     = 'MANAGE_PLATFORMS';
export const VIEW_PLATFORM_ALERTS = 'VIEW_PLATFORM_ALERTS';

// Coding Tracker   (mirrors /api/coding-tracker/*)
export const MANAGE_BATCHES    = 'MANAGE_BATCHES';
export const VIEW_BATCHES      = 'VIEW_BATCHES';
export const MANAGE_STUDENTS   = 'MANAGE_STUDENTS';
export const VIEW_STUDENTS     = 'VIEW_STUDENTS';
export const CREATE_ASSIGNMENT = 'CREATE_ASSIGNMENT';
export const EDIT_ASSIGNMENT   = 'EDIT_ASSIGNMENT';
export const DELETE_ASSIGNMENT = 'DELETE_ASSIGNMENT';
export const VIEW_ASSIGNMENTS  = 'VIEW_ASSIGNMENTS';

// Submissions & Progress
export const VIEW_SUBMISSIONS = 'VIEW_SUBMISSIONS';
export const SUBMIT_SOLUTION  = 'SUBMIT_SOLUTION';

// Plagiarism       (mirrors /api/leetcode/plagiarism-check etc.)
export const RUN_PLAGIARISM_CHECK    = 'RUN_PLAGIARISM_CHECK';
export const VIEW_PLAGIARISM_RESULTS = 'VIEW_PLAGIARISM_RESULTS';

// Grading / Dashboard
export const VIEW_BATCH_DASHBOARD = 'VIEW_BATCH_DASHBOARD';
export const GRADE_SUBMISSIONS    = 'GRADE_SUBMISSIONS';

// Profile
export const EDIT_OWN_PROFILE = 'EDIT_OWN_PROFILE';

/**
 * Master ordered list of every known permission.
 * Drives the checkbox list in the Access Control page.
 */
export const ALL_PERMISSIONS = [
  MANAGE_USERS, VIEW_USERS, CREATE_USERS, EDIT_USERS, DELETE_USERS,
  RESET_PASSWORD, TOGGLE_USER_STATUS,
  MANAGE_PLATFORMS, VIEW_PLATFORM_ALERTS,
  MANAGE_BATCHES, VIEW_BATCHES, MANAGE_STUDENTS, VIEW_STUDENTS,
  CREATE_ASSIGNMENT, EDIT_ASSIGNMENT, DELETE_ASSIGNMENT, VIEW_ASSIGNMENTS,
  VIEW_SUBMISSIONS, SUBMIT_SOLUTION,
  RUN_PLAGIARISM_CHECK, VIEW_PLAGIARISM_RESULTS,
  VIEW_BATCH_DASHBOARD, GRADE_SUBMISSIONS,
  EDIT_OWN_PROFILE,
];

// ─── Default role → permission map ───────────────────────────────────────────
// This is the baseline. At runtime the app reads effective permissions from
// localStorage (via loadOverrides / saveOverrides below), falling back here
// when no override exists.

export const rolePermissions = {
  admin: [
    MANAGE_USERS, VIEW_USERS, CREATE_USERS, EDIT_USERS, DELETE_USERS,
    RESET_PASSWORD, TOGGLE_USER_STATUS,
    MANAGE_PLATFORMS, VIEW_PLATFORM_ALERTS,
    MANAGE_BATCHES, VIEW_BATCHES, MANAGE_STUDENTS, VIEW_STUDENTS,
    CREATE_ASSIGNMENT, EDIT_ASSIGNMENT, DELETE_ASSIGNMENT, VIEW_ASSIGNMENTS,
    VIEW_SUBMISSIONS,
    RUN_PLAGIARISM_CHECK, VIEW_PLAGIARISM_RESULTS,
    VIEW_BATCH_DASHBOARD, GRADE_SUBMISSIONS,
    EDIT_OWN_PROFILE,
  ],
  faculty: [
    VIEW_BATCH_DASHBOARD, GRADE_SUBMISSIONS,
    VIEW_SUBMISSIONS, VIEW_ASSIGNMENTS,
    EDIT_OWN_PROFILE,
  ],
  cbcfaculty: [
    MANAGE_BATCHES, VIEW_BATCHES, MANAGE_STUDENTS, VIEW_STUDENTS,
    CREATE_ASSIGNMENT, EDIT_ASSIGNMENT, DELETE_ASSIGNMENT, VIEW_ASSIGNMENTS,
    VIEW_SUBMISSIONS,
    RUN_PLAGIARISM_CHECK, VIEW_PLAGIARISM_RESULTS,
    EDIT_OWN_PROFILE,
  ],
  student: [
    SUBMIT_SOLUTION,
    EDIT_OWN_PROFILE,
  ],
};

// ─── localStorage persistence ─────────────────────────────────────────────────

const STORAGE_KEY = 'stacktrack_permission_overrides';

/**
 * Load admin-saved permission overrides from localStorage.
 * Returns null if nothing has been saved yet.
 *
 * @returns {{ [role: string]: string[] } | null}
 */
export function loadOverrides() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Persist an edited permission map to localStorage so that
 * hasPermission() and getPermissions() use it immediately.
 *
 * @param {{ [role: string]: string[] }} overrideMap
 */
export function saveOverrides(overrideMap) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(overrideMap));
  } catch (e) {
    console.error('[accessControl] Failed to save overrides:', e);
  }
}

/**
 * Remove all localStorage overrides, reverting to the defaults in
 * rolePermissions above.
 */
export function clearOverrides() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

// ─── Runtime helpers ──────────────────────────────────────────────────────────

/**
 * Returns the live permission list for a role.
 * If the admin has saved overrides via the Access Control page, those
 * are returned; otherwise the default from rolePermissions is used.
 *
 * @param {string} role
 * @returns {string[]}
 */
export function getEffectivePermissions(role) {
  const overrides = loadOverrides();
  if (overrides && Object.prototype.hasOwnProperty.call(overrides, role)) {
    return overrides[role];
  }
  return rolePermissions[role] ?? [];
}

/**
 * Check whether a role currently has a specific permission,
 * respecting any saved overrides.
 *
 * @param {string} userRole
 * @param {string} permission
 * @returns {boolean}
 */
export function hasPermission(userRole, permission) {
  return getEffectivePermissions(userRole).includes(permission);
}

/**
 * Return the full live permission list for a role (with overrides applied).
 *
 * @param {string} userRole
 * @returns {string[]}
 */
export function getPermissions(userRole) {
  return getEffectivePermissions(userRole);
}

/**
 * Return every role that currently holds a given permission.
 *
 * @param {string} permission
 * @returns {string[]}
 */
export function getRolesWithPermission(permission) {
  return Object.keys(rolePermissions).filter((role) =>
    getEffectivePermissions(role).includes(permission)
  );
}
